import { ColumnsType } from 'antd/lib/table';
import { handleNullValues } from 'components/ui/base/table/TableUtils';
import { DeclarationStatuses } from 'store/declarations/enums/common/declaration-status';
import { GoodsShipmentItem } from 'store/declarations/ireland/import-declaration';
import getItemNumberColumn from 'views/declarations/common/getItemNumberColumn';
import { getCurrencySymbol } from 'utils/common';
import { getTaxPriority } from 'views/declarations/declaration-tabs/vat-and-duty/utils';

const handleCommodityCode = (commodity?: string, taric?: string): string => {
    if (commodity && taric) return `${commodity}${taric}`;
    if (commodity) return commodity;
    return '-';
};

const formatValueWithCurrencySymbol = (val: string | undefined, currencySymbol: string | undefined) => {
    if (!val) return '-';
    return currencySymbol + val;
};

const getH1ProductsTableColumns = (
    invoiceCurrency: string | undefined,
    getNameFromCountryCode: (code?: string) => string | undefined,
    declarationStatus: DeclarationStatuses | undefined
): ColumnsType<any> => [
    getItemNumberColumn((record: GoodsShipmentItem) => record.goodsItemNumber, 'GOODS_ITEM_NUMBER'),
    {
        title: 'Code & Description',
        key: 'COMBINED_NOMENCLATURE_CODE',
        render: (_, record: GoodsShipmentItem) => {
            const code = handleCommodityCode(
                record?.goodsInformation?.combinedNomenclatureCode,
                record.goodsInformation?.taricCode
            );
            const description = record?.goodsInformation?.goodsDescription;
            return (
                <div>
                    <div>{handleNullValues(code)}</div>
                    {description != null && <div>{handleNullValues(description)}</div>}
                </div>
            );
        },
        sorter: true,
    },
    {
        title: 'Country of Origin',
        key: 'COUNTRY_ORIGIN',
        render: (_, record: GoodsShipmentItem) => getNameFromCountryCode(record.countryOrigin) ?? '-',
        sorter: true,
    },
    {
        title: 'Packages',
        key: 'packages',
        render: (_, record: GoodsShipmentItem) => {
            const packages = record.goodsInformation?.packaging;

            if (!packages?.length) return null;

            const entries = packages.map((pkg) => {
                if (!pkg.typePackage || !pkg.packageNumber) return null;

                return (
                    <div key={pkg.id ?? `${pkg.typePackage}-${pkg.packageNumber}`}>
                        {pkg.packageNumber} {pkg.typePackage}
                    </div>
                );
            });

            return entries.length ? <div>{entries}</div> : null;
        },
    },
    {
        title: 'Net/Gross Mass',
        key: 'netAndGrossMass',
        align: 'right',
        render: (_, record: GoodsShipmentItem) => {
            const netMass = record?.goodsInformation?.netMass;
            const grossMass = record?.goodsInformation?.grossMass;

            const netMassFormatted = netMass != null ? `${parseFloat(String(netMass)).toFixed(6)} kg` : '-';
            const grossMassFormatted = grossMass != null ? `${parseFloat(String(grossMass)).toFixed(6)} kg` : '-';

            return (
                <div>
                    <div>{netMassFormatted}</div>
                    <div>{grossMassFormatted}</div>
                </div>
            );
        },
    },
    {
        title: 'Value',
        key: 'ITEM_AMOUNT',
        sorter: true,
        render: (_, record: GoodsShipmentItem) => {
            const itemPrice = record?.itemAmount;
            const currencySymbol = getCurrencySymbol(invoiceCurrency);
            const value = formatValueWithCurrencySymbol(itemPrice, currencySymbol);
            return <div style={{ textAlign: 'right' }}>{value}</div>;
        },
    },
    {
        title: 'Add/Ded',
        key: 'additionsAndDeductions',
        width: 1,
        render: (_, record: GoodsShipmentItem) => {
            const items = record?.additionsDeductions;

            if (!items?.length) return '-';
            return (
                <div>
                    {items.map((item) => {
                        const currencySymbol = getCurrencySymbol(item?.currency);
                        const amount = item?.currencyAmount && currencySymbol.concat(item.currencyAmount);

                        return (
                            <li
                                style={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 8 }}
                                key={item.id}
                            >
                                <span>{item.additionsDeductionsCode}</span> <span>{amount}</span>
                            </li>
                        );
                    })}
                </div>
            );
        },
    },
    {
        title: 'VAT/Duty',
        key: 'vatAndDuty',
        render: (_, record: GoodsShipmentItem) => {
            const taxes = declarationStatus === DeclarationStatuses.RELEASED ? record?.taxBox43Bis : record?.localTaxes;

            if (!taxes?.length) return '-';

            const sorted = [...taxes].sort((a, b) => getTaxPriority(a.boxTaxType) - getTaxPriority(b.boxTaxType));

            return (
                <div>
                    {sorted.map((tax) => {
                        return (
                            <div>
                                {tax.boxTaxType} - {tax.boxTaxPayableAmount}
                            </div>
                        );
                    })}
                </div>
            );
        },
    },
];

export default getH1ProductsTableColumns;
